/**
 * name: @storyblok/vue
 * (c) 2025
 * description: SDK to integrate Storyblok into your project using Vue.
 * author: Storyblok
 */
import { defineComponent as Y, ref as P, resolveDynamicComponent as D, inject as de, createBlock as W, openBlock as X, mergeProps as pe, h as Q, createTextVNode as ge, watch as fe, onMounted as me, defineAsyncComponent as ye } from "vue";
let F = !1;
const q = [], be = (s) => new Promise((e, t) => {
  if (typeof window > "u" || (window.storyblokRegisterEvent = (o) => {
    if (window.location === window.parent.location) {
      console.warn("You are not in Draft Mode or in the Visual Editor.");
      return;
    }
    F ? o() : q.push(o);
  }, document.getElementById("storyblok-javascript-bridge")))
    return;
  const r = document.createElement("script");
  r.async = !0, r.src = s, r.id = "storyblok-javascript-bridge", r.onerror = (o) => t(o), r.onload = (o) => {
    q.forEach((n) => n()), F = !0, e(o);
  }, document.getElementsByTagName("head")[0].appendChild(r);
});
var ke = Object.defineProperty, ve = (s, e, t) => e in s ? ke(s, e, { enumerable: !0, configurable: !0, writable: !0, value: t }) : s[e] = t, m = (s, e, t) => ve(s, typeof e != "symbol" ? e + "" : e, t);
class $e extends Error {
  constructor(e) {
    super(e), this.name = "AbortError";
  }
}
function Te(s, e, t) {
  if (!Number.isFinite(e))
    throw new TypeError("Expected `limit` to be a finite number");
  if (!Number.isFinite(t))
    throw new TypeError("Expected `interval` to be a finite number");
  const r = [];
  let o = [], n = 0, a = !1;
  const i = async () => {
    n++;
    const h = r.shift();
    if (h)
      try {
        const p = await s(...h.args);
        h.resolve(p);
      } catch (p) {
        h.reject(p);
      }
    const d = setTimeout(() => {
      n--, r.length > 0 && i(), o = o.filter((p) => p !== d);
    }, t);
    o.includes(d) || o.push(d);
  }, l = (...h) => a ? Promise.reject(
    new Error(
      "Throttled function is already aborted and not accepting new promises"
    )
  ) : new Promise((d, p) => {
    r.push({
      resolve: d,
      reject: p,
      args: h
    }), n < e && i();
  });
  return l.abort = () => {
    a = !0, o.forEach(clearTimeout), o = [], r.forEach(
      (h) => h.reject(() => new $e("Throttle function aborted"))
    ), r.length = 0;
  }, l;
}
class N {
  constructor() {
    m(this, "isCDNUrl", (e = "") => e.includes("/cdn/")), m(this, "getOptionsPage", (e, t = 25, r = 1) => ({
      ...e,
      per_page: t,
      page: r
    })), m(this, "delay", (e) => new Promise((t) => setTimeout(t, e))), m(this, "arrayFrom", (e = 0, t) => Array.from({ length: e }, t)), m(this, "range", (e = 0, t = e) => {
      const r = Math.abs(t - e) || 0, o = e < t ? 1 : -1;
      return this.arrayFrom(r, (n, a) => a * o + e);
    }), m(this, "asyncMap", async (e, t) => Promise.all(e.map(t))), m(this, "flatMap", (e = [], t) => e.map(t).reduce((r, o) => [...r, ...o], [])), m(this, "escapeHTML", function(e) {
      const t = {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;"
      }, r = /[&<>"']/g, o = new RegExp(r.source);
      return e && o.test(e) ? e.replace(r, (n) => t[n]) : e;
    });
  }
  /**
   * @method stringify
   * @param  {object} params
   * @param  {string} prefix
   * @param  {boolean} isArray
   * @return {string} Stringified object
   */
  stringify(e, t, r) {
    const o = [];
    for (const n in e) {
      if (!Object.prototype.hasOwnProperty.call(e, n))
        continue;
      const a = e[n];
      if (a == null)
        continue;
      const i = r ? "" : encodeURIComponent(n);
      let l;
      typeof a == "object" ? l = this.stringify(
        a,
        t ? t + encodeURIComponent(`[${i}]`) : i,
        Array.isArray(a)
      ) : l = `${t ? t + encodeURIComponent(`[${i}]`) : i}=${encodeURIComponent(a)}`, o.push(l);
    }
    return o.join("&");
  }
  /**
   * @method getRegionURL
   * @param  {string} regionCode region code, could be eu, us, cn, ap or ca
   * @return {string} The base URL of the region
   */
  getRegionURL(e) {
    const t = "api.storyblok.com", r = "api-us.storyblok.com", o = "app.storyblokchina.cn", n = "api-ap.storyblok.com", a = "api-ca.storyblok.com";
    switch (e) {
      case "us":
        return r;
      case "cn":
        return o;
      case "ap":
        return n;
      case "ca":
        return a;
      default:
        return t;
    }
  }
}
const we = function(s, e) {
  const t = {};
  for (const r in s) {
    const o = s[r];
    e.includes(r) && o !== null && (t[r] = o);
  }
  return t;
}, Re = (s) => s === "email", _e = () => ({
  singleTag: "hr"
}), Se = () => ({
  tag: "blockquote"
}), Ee = () => ({
  tag: "ul"
}), xe = (s) => ({
  tag: [
    "pre",
    {
      tag: "code",
      attrs: s.attrs
    }
  ]
}), je = () => ({
  singleTag: "br"
}), Ae = (s) => ({
  tag: `h${s.attrs.level}`
}), Ie = (s) => ({
  singleTag: [
    {
      tag: "img",
      attrs: we(s.attrs, ["src", "alt", "title"])
    }
  ]
}), Ce = () => ({
  tag: "li"
}), Le = () => ({
  tag: "ol"
}), Oe = () => ({
  tag: "p"
}), Pe = (s) => ({
  tag: [
    {
      tag: "span",
      attrs: {
        "data-type": "emoji",
        "data-name": s.attrs.name,
        emoji: s.attrs.emoji
      }
    }
  ]
}), Ne = () => ({
  tag: "b"
}), Me = () => ({
  tag: "s"
}), He = () => ({
  tag: "u"
}), De = () => ({
  tag: "strong"
}), Ue = () => ({
  tag: "code"
}), Be = () => ({
  tag: "i"
}), ze = (s) => {
  if (!s.attrs)
    return {
      tag: ""
    };
  const e = new N().escapeHTML, t = { ...s.attrs }, { linktype: r = "url" } = s.attrs;
  if (delete t.linktype, t.href && (t.href = e(s.attrs.href || "")), Re(r) && (t.href = `mailto:${t.href}`), t.anchor && (t.href = `${t.href}#${t.anchor}`, delete t.anchor), t.custom) {
    for (const o in t.custom)
      t[o] = t.custom[o];
    delete t.custom;
  }
  return {
    tag: [
      {
        tag: "a",
        attrs: t
      }
    ]
  };
}, Fe = (s) => ({
  tag: [
    {
      tag: "span",
      attrs: s.attrs
    }
  ]
}), qe = () => ({
  tag: "sub"
}), Ve = () => ({
  tag: "sup"
}), Ge = (s) => ({
  tag: [
    {
      tag: "span",
      attrs: s.attrs
    }
  ]
}), Ke = (s) => {
  var e;
  return (e = s.attrs) != null && e.color ? {
    tag: [
      {
        tag: "span",
        attrs: {
          style: `background-color:${s.attrs.color};`
        }
      }
    ]
  } : {
    tag: ""
  };
}, Je = (s) => {
  var e;
  return (e = s.attrs) != null && e.color ? {
    tag: [
      {
        tag: "span",
        attrs: {
          style: `color:${s.attrs.color}`
        }
      }
    ]
  } : {
    tag: ""
  };
}, Ye = {
  nodes: {
    horizontal_rule: _e,
    blockquote: Se,
    bullet_list: Ee,
    code_block: xe,
    hard_break: je,
    heading: Ae,
    image: Ie,
    list_item: Ce,
    ordered_list: Le,
    paragraph: Oe,
    emoji: Pe
  },
  marks: {
    bold: Ne,
    strike: Me,
    underline: He,
    strong: De,
    code: Ue,
    italic: Be,
    link: ze,
    styled: Fe,
    subscript: qe,
    superscript: Ve,
    anchor: Ge,
    highlight: Ke,
    textStyle: Je
  }
}, We = function(s) {
  const e = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;"
  }, t = /[&<>"']/g, r = new RegExp(t.source);
  return s && r.test(s) ? s.replace(t, (o) => e[o]) : s;
};
let V = !1;
class M {
  constructor(e) {
    m(this, "marks"), m(this, "nodes"), e || (e = Ye), this.marks = e.marks || [], this.nodes = e.nodes || [];
  }
  addNode(e, t) {
    this.nodes[e] = t;
  }
  addMark(e, t) {
    this.marks[e] = t;
  }
  render(e, t = { optimizeImages: !1 }, r = !0) {
    if (!V && r && (console.warn(
      "Warning ⚠️: The RichTextResolver class is deprecated and will be removed in the next major release. Please use the `@storyblok/richtext` package instead. https://github.com/storyblok/richtext/"
    ), V = !0), e && e.content && Array.isArray(e.content)) {
      let o = "";
      return e.content.forEach((n) => {
        o += this.renderNode(n);
      }), t.optimizeImages ? this.optimizeImages(o, t.optimizeImages) : o;
    }
    return console.warn(
      `The render method must receive an Object with a "content" field.
      The "content" field must be an array of nodes as the type ISbRichtext.
      ISbRichtext:
        content?: ISbRichtext[]
        marks?: ISbRichtext[]
        attrs?: any
        text?: string
        type: string
        
        Example:
        {
          content: [
            {
              content: [
                {
                  text: 'Hello World',
                  type: 'text'
                }
              ],
              type: 'paragraph'
            }
          ],
          type: 'doc'
        }`
    ), "";
  }
  optimizeImages(e, t) {
    let r = 0, o = 0, n = "", a = "";
    typeof t != "boolean" && (typeof t.width == "number" && t.width > 0 && (n += `width="${t.width}" `, r = t.width), typeof t.height == "number" && t.height > 0 && (n += `height="${t.height}" `, o = t.height), (t.loading === "lazy" || t.loading === "eager") && (n += `loading="${t.loading}" `), typeof t.class == "string" && t.class.length > 0 && (n += `class="${t.class}" `), t.filters && (typeof t.filters.blur == "number" && t.filters.blur >= 0 && t.filters.blur <= 100 && (a += `:blur(${t.filters.blur})`), typeof t.filters.brightness == "number" && t.filters.brightness >= -100 && t.filters.brightness <= 100 && (a += `:brightness(${t.filters.brightness})`), t.filters.fill && (t.filters.fill.match(/[0-9A-F]{6}/gi) || t.filters.fill === "transparent") && (a += `:fill(${t.filters.fill})`), t.filters.format && ["webp", "png", "jpeg"].includes(t.filters.format) && (a += `:format(${t.filters.format})`), typeof t.filters.grayscale == "boolean" && t.filters.grayscale && (a += ":grayscale()"), typeof t.filters.quality == "number" && t.filters.quality >= 0 && t.filters.quality <= 100 && (a += `:quality(${t.filters.quality})`), t.filters.rotate && [90, 180, 270].includes(t.filters.rotate) && (a += `:rotate(${t.filters.rotate})`), a.length > 0 && (a = `/filters${a}`))), n.length > 0 && (e = e.replace(/<img/g, `<img ${n.trim()}`));
    const i = r > 0 || o > 0 || a.length > 0 ? `${r}x${o}${a}` : "";
    return e = e.replace(
      /a.storyblok.com\/f\/(\d+)\/([^.]+)\.(gif|jpg|jpeg|png|tif|bmp)/g,
      `a.storyblok.com/f/$1/$2.$3/m/${i}`
    ), typeof t != "boolean" && (t.sizes || t.srcset) && (e = e.replace(/<img.*?src=["|'](.*?)["|']/g, (l) => {
      var h, d;
      const p = l.match(
        /a.storyblok.com\/f\/(\d+)\/([^.]+)\.(gif|jpg|jpeg|png|tif|bmp)/g
      );
      if (p && p.length > 0) {
        const b = {
          srcset: (h = t.srcset) == null ? void 0 : h.map((y) => {
            if (typeof y == "number")
              return `//${p}/m/${y}x0${a} ${y}w`;
            if (typeof y == "object" && y.length === 2) {
              let E = 0, I = 0;
              return typeof y[0] == "number" && (E = y[0]), typeof y[1] == "number" && (I = y[1]), `//${p}/m/${E}x${I}${a} ${E}w`;
            }
            return "";
          }).join(", "),
          sizes: (d = t.sizes) == null ? void 0 : d.map((y) => y).join(", ")
        };
        let S = "";
        return b.srcset && (S += `srcset="${b.srcset}" `), b.sizes && (S += `sizes="${b.sizes}" `), l.replace(/<img/g, `<img ${S.trim()}`);
      }
      return l;
    })), e;
  }
  renderNode(e) {
    const t = [];
    e.marks && e.marks.forEach((o) => {
      const n = this.getMatchingMark(o);
      n && n.tag !== "" && t.push(this.renderOpeningTag(n.tag));
    });
    const r = this.getMatchingNode(e);
    return r && r.tag && t.push(this.renderOpeningTag(r.tag)), e.content ? e.content.forEach((o) => {
      t.push(this.renderNode(o));
    }) : e.text ? t.push(We(e.text)) : r && r.singleTag ? t.push(this.renderTag(r.singleTag, " /")) : r && r.html ? t.push(r.html) : e.type === "emoji" && t.push(this.renderEmoji(e)), r && r.tag && t.push(this.renderClosingTag(r.tag)), e.marks && e.marks.slice(0).reverse().forEach((o) => {
      const n = this.getMatchingMark(o);
      n && n.tag !== "" && t.push(this.renderClosingTag(n.tag));
    }), t.join("");
  }
  renderTag(e, t) {
    return e.constructor === String ? `<${e}${t}>` : e.map((r) => {
      if (r.constructor === String)
        return `<${r}${t}>`;
      {
        let o = `<${r.tag}`;
        if (r.attrs) {
          for (const n in r.attrs)
            if (Object.prototype.hasOwnProperty.call(r.attrs, n)) {
              const a = r.attrs[n];
              a !== null && (o += ` ${n}="${a}"`);
            }
        }
        return `${o}${t}>`;
      }
    }).join("");
  }
  renderOpeningTag(e) {
    return this.renderTag(e, "");
  }
  renderClosingTag(e) {
    return e.constructor === String ? `</${e}>` : e.slice(0).reverse().map((t) => t.constructor === String ? `</${t}>` : `</${t.tag}>`).join("");
  }
  getMatchingNode(e) {
    const t = this.nodes[e.type];
    if (typeof t == "function")
      return t(e);
  }
  getMatchingMark(e) {
    const t = this.marks[e.type];
    if (typeof t == "function")
      return t(e);
  }
  renderEmoji(e) {
    if (e.attrs.emoji)
      return e.attrs.emoji;
    const t = [
      {
        tag: "img",
        attrs: {
          src: e.attrs.fallbackImage,
          draggable: "false",
          loading: "lazy",
          align: "absmiddle"
        }
      }
    ];
    return this.renderTag(t, " /");
  }
}
class Xe {
  constructor(e) {
    m(this, "baseURL"), m(this, "timeout"), m(this, "headers"), m(this, "responseInterceptor"), m(this, "fetch"), m(this, "ejectInterceptor"), m(this, "url"), m(this, "parameters"), m(this, "fetchOptions"), this.baseURL = e.baseURL, this.headers = e.headers || new Headers(), this.timeout = e != null && e.timeout ? e.timeout * 1e3 : 0, this.responseInterceptor = e.responseInterceptor, this.fetch = (...t) => e.fetch ? e.fetch(...t) : fetch(...t), this.ejectInterceptor = !1, this.url = "", this.parameters = {}, this.fetchOptions = {};
  }
  /**
   *
   * @param url string
   * @param params ISbStoriesParams
   * @returns Promise<ISbResponse | Error>
   */
  get(e, t) {
    return this.url = e, this.parameters = t, this._methodHandler("get");
  }
  post(e, t) {
    return this.url = e, this.parameters = t, this._methodHandler("post");
  }
  put(e, t) {
    return this.url = e, this.parameters = t, this._methodHandler("put");
  }
  delete(e, t) {
    return this.url = e, this.parameters = t ?? {}, this._methodHandler("delete");
  }
  async _responseHandler(e) {
    const t = [], r = {
      data: {},
      headers: {},
      status: 0,
      statusText: ""
    };
    e.status !== 204 && await e.json().then((o) => {
      r.data = o;
    });
    for (const o of e.headers.entries())
      t[o[0]] = o[1];
    return r.headers = { ...t }, r.status = e.status, r.statusText = e.statusText, r;
  }
  async _methodHandler(e) {
    let t = `${this.baseURL}${this.url}`, r = null;
    if (e === "get") {
      const l = new N();
      t = `${this.baseURL}${this.url}?${l.stringify(
        this.parameters
      )}`;
    } else
      r = JSON.stringify(this.parameters);
    const o = new URL(t), n = new AbortController(), { signal: a } = n;
    let i;
    this.timeout && (i = setTimeout(() => n.abort(), this.timeout));
    try {
      const l = await this.fetch(`${o}`, {
        method: e,
        headers: this.headers,
        body: r,
        signal: a,
        ...this.fetchOptions
      });
      this.timeout && clearTimeout(i);
      const h = await this._responseHandler(
        l
      );
      return this.responseInterceptor && !this.ejectInterceptor ? this._statusHandler(this.responseInterceptor(h)) : this._statusHandler(h);
    } catch (l) {
      return {
        message: l
      };
    }
  }
  setFetchOptions(e = {}) {
    Object.keys(e).length > 0 && "method" in e && delete e.method, this.fetchOptions = { ...e };
  }
  eject() {
    this.ejectInterceptor = !0;
  }
  _statusHandler(e) {
    const t = /20[0-6]/g;
    return new Promise((r, o) => {
      if (t.test(`${e.status}`))
        return r(e);
      const n = {
        message: e.statusText,
        status: e.status,
        response: Array.isArray(e.data) ? e.data[0] : e.data.error || e.data.slug
      };
      o(n);
    });
  }
}
const G = "SB-Agent", U = {
  defaultAgentName: "SB-JS-CLIENT",
  defaultAgentVersion: "SB-Agent-Version",
  packageVersion: "6.0.0"
};
let L = {};
const x = {};
class Qe {
  /**
   *
   * @param config ISbConfig interface
   * @param pEndpoint string, optional
   */
  constructor(e, t) {
    m(this, "client"), m(this, "maxRetries"), m(this, "retriesDelay"), m(this, "throttle"), m(this, "accessToken"), m(this, "cache"), m(this, "helpers"), m(this, "resolveCounter"), m(this, "relations"), m(this, "links"), m(this, "richTextResolver"), m(this, "resolveNestedRelations"), m(this, "stringifiedStoriesCache");
    let r = e.endpoint || t;
    if (!r) {
      const a = new N().getRegionURL, i = e.https === !1 ? "http" : "https";
      e.oauthToken ? r = `${i}://${a(e.region)}/v1` : r = `${i}://${a(e.region)}/v2`;
    }
    const o = new Headers();
    o.set("Content-Type", "application/json"), o.set("Accept", "application/json"), e.headers && (e.headers.constructor.name === "Headers" ? e.headers.entries().toArray() : Object.entries(e.headers)).forEach(([a, i]) => {
      o.set(a, i);
    }), o.has(G) || (o.set(G, U.defaultAgentName), o.set(
      U.defaultAgentVersion,
      U.packageVersion
    ));
    let n = 5;
    e.oauthToken && (o.set("Authorization", e.oauthToken), n = 3), e.rateLimit && (n = e.rateLimit), e.richTextSchema ? this.richTextResolver = new M(e.richTextSchema) : this.richTextResolver = new M(), e.componentResolver && this.setComponentResolver(e.componentResolver), this.maxRetries = e.maxRetries || 10, this.retriesDelay = 300, this.throttle = Te(
      this.throttledRequest.bind(this),
      n,
      1e3
    ), this.accessToken = e.accessToken || "", this.relations = {}, this.links = {}, this.cache = e.cache || { clear: "manual" }, this.helpers = new N(), this.resolveCounter = 0, this.resolveNestedRelations = e.resolveNestedRelations || !0, this.stringifiedStoriesCache = {}, this.client = new Xe({
      baseURL: r,
      timeout: e.timeout || 0,
      headers: o,
      responseInterceptor: e.responseInterceptor,
      fetch: e.fetch
    });
  }
  setComponentResolver(e) {
    this.richTextResolver.addNode("blok", (t) => {
      let r = "";
      return t.attrs.body && t.attrs.body.forEach((o) => {
        r += e(o.component, o);
      }), {
        html: r
      };
    });
  }
  parseParams(e) {
    return e.token || (e.token = this.getToken()), e.cv || (e.cv = x[e.token]), Array.isArray(e.resolve_relations) && (e.resolve_relations = e.resolve_relations.join(",")), typeof e.resolve_relations < "u" && (e.resolve_level = 2), e;
  }
  factoryParamOptions(e, t) {
    return this.helpers.isCDNUrl(e) ? this.parseParams(t) : t;
  }
  makeRequest(e, t, r, o, n) {
    const a = this.factoryParamOptions(
      e,
      this.helpers.getOptionsPage(t, r, o)
    );
    return this.cacheResponse(e, a, void 0, n);
  }
  get(e, t, r) {
    t || (t = {});
    const o = `/${e}`, n = this.factoryParamOptions(o, t);
    return this.cacheResponse(o, n, void 0, r);
  }
  async getAll(e, t, r, o) {
    const n = (t == null ? void 0 : t.per_page) || 25, a = `/${e}`.replace(/\/$/, ""), i = r ?? a.substring(a.lastIndexOf("/") + 1), l = 1, h = await this.makeRequest(
      a,
      t,
      n,
      l,
      o
    ), d = h.total ? Math.ceil(h.total / n) : 1, p = await this.helpers.asyncMap(
      this.helpers.range(l, d),
      (b) => this.makeRequest(a, t, n, b + 1, o)
    );
    return this.helpers.flatMap([h, ...p], (b) => Object.values(b.data[i]));
  }
  post(e, t, r) {
    const o = `/${e}`;
    return Promise.resolve(
      this.throttle("post", o, t, r)
    );
  }
  put(e, t, r) {
    const o = `/${e}`;
    return Promise.resolve(
      this.throttle("put", o, t, r)
    );
  }
  delete(e, t, r) {
    t || (t = {});
    const o = `/${e}`;
    return Promise.resolve(
      this.throttle("delete", o, t, r)
    );
  }
  getStories(e, t) {
    return this._addResolveLevel(e), this.get("cdn/stories", e, t);
  }
  getStory(e, t, r) {
    return this._addResolveLevel(t), this.get(`cdn/stories/${e}`, t, r);
  }
  getToken() {
    return this.accessToken;
  }
  ejectInterceptor() {
    this.client.eject();
  }
  _addResolveLevel(e) {
    typeof e.resolve_relations < "u" && (e.resolve_level = 2);
  }
  _cleanCopy(e) {
    return JSON.parse(JSON.stringify(e));
  }
  _insertLinks(e, t, r) {
    const o = e[t];
    o && o.fieldtype === "multilink" && o.linktype === "story" && typeof o.id == "string" && this.links[r][o.id] ? o.story = this._cleanCopy(this.links[r][o.id]) : o && o.linktype === "story" && typeof o.uuid == "string" && this.links[r][o.uuid] && (o.story = this._cleanCopy(this.links[r][o.uuid]));
  }
  /**
   *
   * @param resolveId A counter number as a string
   * @param uuid The uuid of the story
   * @returns string | object
   */
  getStoryReference(e, t) {
    return this.relations[e][t] ? JSON.parse(this.stringifiedStoriesCache[t] || JSON.stringify(this.relations[e][t])) : t;
  }
  /**
   * Resolves a field's value by replacing UUIDs with their corresponding story references
   * @param jtree - The JSON tree object containing the field to resolve
   * @param treeItem - The key of the field to resolve
   * @param resolveId - The unique identifier for the current resolution context
   *
   * This method handles both single string UUIDs and arrays of UUIDs:
   * - For single strings: directly replaces the UUID with the story reference
   * - For arrays: maps through each UUID and replaces with corresponding story references
   */
  _resolveField(e, t, r) {
    const o = e[t];
    typeof o == "string" ? e[t] = this.getStoryReference(r, o) : Array.isArray(o) && (e[t] = o.map(
      (n) => this.getStoryReference(r, n)
    ).filter(Boolean));
  }
  /**
   * Inserts relations into the JSON tree by resolving references
   * @param jtree - The JSON tree object to process
   * @param treeItem - The current field being processed
   * @param fields - The relation patterns to resolve (string or array of strings)
   * @param resolveId - The unique identifier for the current resolution context
   *
   * This method handles two types of relation patterns:
   * 1. Nested relations: matches fields that end with the current field name
   *    Example: If treeItem is "event_type", it matches patterns like "*.event_type"
   *
   * 2. Direct component relations: matches exact component.field patterns
   *    Example: "event.event_type" for component "event" and field "event_type"
   *
   * The method supports both string and array formats for the fields parameter,
   * allowing flexible specification of relation patterns.
   */
  _insertRelations(e, t, r, o) {
    if (Array.isArray(r) ? r.find((a) => a.endsWith(`.${t}`)) : r.endsWith(`.${t}`)) {
      this._resolveField(e, t, o);
      return;
    }
    const n = e.component ? `${e.component}.${t}` : t;
    (Array.isArray(r) ? r.includes(n) : r === n) && this._resolveField(e, t, o);
  }
  /**
   * Recursively traverses and resolves relations in the story content tree
   * @param story - The story object containing the content to process
   * @param fields - The relation patterns to resolve
   * @param resolveId - The unique identifier for the current resolution context
   */
  iterateTree(e, t, r) {
    const o = (n, a = "") => {
      if (!(!n || n._stopResolving)) {
        if (Array.isArray(n))
          n.forEach((i, l) => o(i, `${a}[${l}]`));
        else if (typeof n == "object")
          for (const i in n) {
            const l = a ? `${a}.${i}` : i;
            (n.component && n._uid || n.type === "link") && (this._insertRelations(n, i, t, r), this._insertLinks(n, i, r)), o(n[i], l);
          }
      }
    };
    o(e.content);
  }
  async resolveLinks(e, t, r) {
    let o = [];
    if (e.link_uuids) {
      const n = e.link_uuids.length, a = [], i = 50;
      for (let l = 0; l < n; l += i) {
        const h = Math.min(n, l + i);
        a.push(e.link_uuids.slice(l, h));
      }
      for (let l = 0; l < a.length; l++)
        (await this.getStories({
          per_page: i,
          language: t.language,
          version: t.version,
          starts_with: t.starts_with,
          by_uuids: a[l].join(",")
        })).data.stories.forEach(
          (h) => {
            o.push(h);
          }
        );
    } else
      o = e.links;
    o.forEach((n) => {
      this.links[r][n.uuid] = {
        ...n,
        _stopResolving: !0
      };
    });
  }
  async resolveRelations(e, t, r) {
    let o = [];
    if (e.rel_uuids) {
      const n = e.rel_uuids.length, a = [], i = 50;
      for (let l = 0; l < n; l += i) {
        const h = Math.min(n, l + i);
        a.push(e.rel_uuids.slice(l, h));
      }
      for (let l = 0; l < a.length; l++)
        (await this.getStories({
          per_page: i,
          language: t.language,
          version: t.version,
          starts_with: t.starts_with,
          by_uuids: a[l].join(","),
          excluding_fields: t.excluding_fields
        })).data.stories.forEach((h) => {
          o.push(h);
        });
      o.length > 0 && (e.rels = o, delete e.rel_uuids);
    } else
      o = e.rels;
    o && o.length > 0 && o.forEach((n) => {
      this.relations[r][n.uuid] = {
        ...n,
        _stopResolving: !0
      };
    });
  }
  /**
   *
   * @param responseData
   * @param params
   * @param resolveId
   * @description Resolves the relations and links of the stories
   * @returns Promise<void>
   *
   */
  async resolveStories(e, t, r) {
    var o, n;
    let a = [];
    if (this.links[r] = {}, this.relations[r] = {}, typeof t.resolve_relations < "u" && t.resolve_relations.length > 0 && (typeof t.resolve_relations == "string" && (a = t.resolve_relations.split(",")), await this.resolveRelations(e, t, r)), t.resolve_links && ["1", "story", "url", "link"].includes(t.resolve_links) && ((o = e.links) != null && o.length || (n = e.link_uuids) != null && n.length) && await this.resolveLinks(e, t, r), this.resolveNestedRelations)
      for (const i in this.relations[r])
        this.iterateTree(
          this.relations[r][i],
          a,
          r
        );
    e.story ? this.iterateTree(e.story, a, r) : e.stories.forEach((i) => {
      this.iterateTree(i, a, r);
    }), this.stringifiedStoriesCache = {}, delete this.links[r], delete this.relations[r];
  }
  async cacheResponse(e, t, r, o) {
    const n = this.helpers.stringify({ url: e, params: t }), a = this.cacheProvider();
    if (t.version === "published" && e !== "/cdn/spaces/me") {
      const i = await a.get(n);
      if (i)
        return Promise.resolve(i);
    }
    return new Promise(async (i, l) => {
      var h;
      try {
        const d = await this.throttle(
          "get",
          e,
          t,
          o
        );
        if (d.status !== 200)
          return l(d);
        let p = { data: d.data, headers: d.headers };
        if ((h = d.headers) != null && h["per-page"] && (p = Object.assign({}, p, {
          perPage: d.headers["per-page"] ? Number.parseInt(d.headers["per-page"]) : 0,
          total: d.headers["per-page"] ? Number.parseInt(d.headers.total) : 0
        })), p.data.story || p.data.stories) {
          const S = this.resolveCounter = ++this.resolveCounter % 1e3;
          await this.resolveStories(p.data, t, `${S}`);
        }
        t.version === "published" && e !== "/cdn/spaces/me" && await a.set(n, p);
        const b = this.cache.clear === "onpreview" && t.version === "draft" || this.cache.clear === "auto";
        return t.token && p.data.cv && (b && x[t.token] && x[t.token] !== p.data.cv && await this.flushCache(), x[t.token] = p.data.cv), i(p);
      } catch (d) {
        if (d.response && d.status === 429 && (r = typeof r > "u" ? 0 : r + 1, r < this.maxRetries))
          return console.log(
            `Hit rate limit. Retrying in ${this.retriesDelay / 1e3} seconds.`
          ), await this.helpers.delay(this.retriesDelay), this.cacheResponse(e, t, r).then(i).catch(l);
        l(d);
      }
    });
  }
  throttledRequest(e, t, r, o) {
    return this.client.setFetchOptions(o), this.client[e](t, r);
  }
  cacheVersions() {
    return x;
  }
  cacheVersion() {
    return x[this.accessToken];
  }
  setCacheVersion(e) {
    this.accessToken && (x[this.accessToken] = e);
  }
  clearCacheVersion() {
    this.accessToken && (x[this.accessToken] = 0);
  }
  cacheProvider() {
    switch (this.cache.type) {
      case "memory":
        return {
          get(e) {
            return Promise.resolve(L[e]);
          },
          getAll() {
            return Promise.resolve(L);
          },
          set(e, t) {
            return L[e] = t, Promise.resolve(void 0);
          },
          flush() {
            return L = {}, Promise.resolve(void 0);
          }
        };
      case "custom":
        if (this.cache.custom)
          return this.cache.custom;
      default:
        return {
          get() {
            return Promise.resolve();
          },
          getAll() {
            return Promise.resolve(void 0);
          },
          set() {
            return Promise.resolve(void 0);
          },
          flush() {
            return Promise.resolve(void 0);
          }
        };
    }
  }
  async flushCache() {
    return await this.cacheProvider().flush(), this.clearCacheVersion(), this;
  }
}
const gt = (s = {}) => {
  const { apiOptions: e } = s;
  if (!e || !e.accessToken) {
    console.error(
      "You need to provide an access token to interact with Storyblok API. Read https://www.storyblok.com/docs/api/content-delivery#topics/authentication"
    );
    return;
  }
  return { storyblokApi: new Qe(e) };
}, Ze = (s) => {
  if (typeof s != "object" || typeof s._editable > "u")
    return {};
  try {
    const e = JSON.parse(
      s._editable.replace(/^<!--#storyblok#/, "").replace(/-->$/, "")
    );
    return e ? {
      "data-blok-c": JSON.stringify(e),
      "data-blok-uid": `${e.id}-${e.uid}`
    } : {};
  } catch {
    return {};
  }
};
function et(s, e) {
  if (!e)
    return { src: s, attrs: {} };
  let t = 0, r = 0;
  const o = {}, n = [];
  function a(l, h, d, p, b) {
    typeof l != "number" || l <= h || l >= d ? console.warn(`[StoryblokRichText] - ${p.charAt(0).toUpperCase() + p.slice(1)} value must be a number between ${h} and ${d} (inclusive)`) : b.push(`${p}(${l})`);
  }
  if (typeof e == "object") {
    if (typeof e.width == "number" && e.width > 0 ? (o.width = e.width, t = e.width) : console.warn("[StoryblokRichText] - Width value must be a number greater than 0"), e.height && typeof e.height == "number" && e.height > 0 ? (o.height = e.height, r = e.height) : console.warn("[StoryblokRichText] - Height value must be a number greater than 0"), e.loading && ["lazy", "eager"].includes(e.loading) && (o.loading = e.loading), e.class && (o.class = e.class), e.filters) {
      const { filters: l } = e || {}, { blur: h, brightness: d, fill: p, format: b, grayscale: S, quality: y, rotate: E } = l || {};
      h && a(h, 0, 100, "blur", n), y && a(y, 0, 100, "quality", n), d && a(d, 0, 100, "brightness", n), p && n.push(`fill(${p})`), S && n.push("grayscale()"), E && [0, 90, 180, 270].includes(e.filters.rotate || 0) && n.push(`rotate(${E})`), b && ["webp", "png", "jpeg"].includes(b) && n.push(`format(${b})`);
    }
    e.srcset && (o.srcset = e.srcset.map((l) => {
      if (typeof l == "number")
        return `${s}/m/${l}x0/${n.length > 0 ? `filters:${n.join(":")}` : ""} ${l}w`;
      if (Array.isArray(l) && l.length === 2) {
        const [h, d] = l;
        return `${s}/m/${h}x${d}/${n.length > 0 ? `filters:${n.join(":")}` : ""} ${h}w`;
      } else {
        console.warn("[StoryblokRichText] - srcset entry must be a number or a tuple of two numbers");
        return;
      }
    }).join(", ")), e.sizes && (o.sizes = e.sizes.join(", "));
  }
  let i = `${s}/m/`;
  return t > 0 && r > 0 && (i = `${i}${t}x${r}/`), n.length > 0 && (i = `${i}filters:${n.join(":")}`), {
    src: i,
    attrs: o
  };
}
var v = /* @__PURE__ */ ((s) => (s.DOCUMENT = "doc", s.HEADING = "heading", s.PARAGRAPH = "paragraph", s.QUOTE = "blockquote", s.OL_LIST = "ordered_list", s.UL_LIST = "bullet_list", s.LIST_ITEM = "list_item", s.CODE_BLOCK = "code_block", s.HR = "horizontal_rule", s.BR = "hard_break", s.IMAGE = "image", s.EMOJI = "emoji", s.COMPONENT = "blok", s.TABLE = "table", s.TABLE_ROW = "tableRow", s.TABLE_CELL = "tableCell", s.TABLE_HEADER = "tableHeader", s))(v || {}), _ = /* @__PURE__ */ ((s) => (s.BOLD = "bold", s.STRONG = "strong", s.STRIKE = "strike", s.UNDERLINE = "underline", s.ITALIC = "italic", s.CODE = "code", s.LINK = "link", s.ANCHOR = "anchor", s.STYLED = "styled", s.SUPERSCRIPT = "superscript", s.SUBSCRIPT = "subscript", s.TEXT_STYLE = "textStyle", s.HIGHLIGHT = "highlight", s))(_ || {}), Z = /* @__PURE__ */ ((s) => (s.TEXT = "text", s))(Z || {}), j = /* @__PURE__ */ ((s) => (s.URL = "url", s.STORY = "story", s.ASSET = "asset", s.EMAIL = "email", s))(j || {});
const tt = [
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr"
], rt = (s = {}) => Object.keys(s).map((e) => `${e}="${s[e]}"`).join(" "), st = (s = {}) => Object.keys(s).map((e) => `${e}: ${s[e]}`).join("; ");
function ot(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}
const O = (s) => Object.fromEntries(Object.entries(s).filter(([e, t]) => t !== void 0));
function K(s, e = {}, t) {
  const r = rt(e), o = r ? `${s} ${r}` : s, n = Array.isArray(t) ? t.join("") : t || "";
  if (s) {
    if (tt.includes(s))
      return `<${o}>`;
  } else return n;
  return `<${o}>${n}</${s}>`;
}
function nt(s = {}) {
  const e = /* @__PURE__ */ new Map(), {
    renderFn: t = K,
    textFn: r = ot,
    resolvers: o = {},
    optimizeImages: n = !1,
    keyedResolvers: a = !1
  } = s, i = t !== K, l = () => ({ render: (c, u = {}, g) => {
    if (a && c) {
      const f = e.get(c) || 0;
      e.set(c, f + 1), u.key = `${c}-${f}`;
    }
    return t(c, u, g);
  } }), h = (c) => (u, g) => {
    const f = u.attrs || {};
    return g.render(c, f, u.children || null);
  }, d = (c, u) => {
    const { src: g, alt: f, title: k, srcset: w, sizes: $ } = c.attrs || {};
    let T = g, R = {};
    if (n) {
      const { src: he, attrs: ue } = et(g, n);
      T = he, R = ue;
    }
    const ce = {
      src: T,
      alt: f,
      title: k,
      srcset: w,
      sizes: $,
      ...R
    };
    return u.render("img", O(ce));
  }, p = (c, u) => {
    const { level: g, ...f } = c.attrs || {};
    return u.render(`h${g}`, f, c.children);
  }, b = (c, u) => {
    var g, f, k, w;
    const $ = u.render("img", {
      src: (g = c.attrs) == null ? void 0 : g.fallbackImage,
      alt: (f = c.attrs) == null ? void 0 : f.alt,
      style: "width: 1.25em; height: 1.25em; vertical-align: text-top",
      draggable: "false",
      loading: "lazy"
    });
    return u.render("span", {
      "data-type": "emoji",
      "data-name": (k = c.attrs) == null ? void 0 : k.name,
      "data-emoji": (w = c.attrs) == null ? void 0 : w.emoji
    }, $);
  }, S = (c, u) => u.render(
    "pre",
    c.attrs || {},
    u.render("code", {}, c.children || "")
  ), y = (c, u = !1) => ({ text: g, attrs: f }, k) => {
    const { class: w, id: $, ...T } = f || {}, R = u ? {
      class: w,
      id: $,
      style: st(T) || void 0
    } : f || {};
    return k.render(c, O(R), g);
  }, E = (c) => H(c), I = (c) => {
    const { marks: u, ...g } = c;
    if ("text" in c) {
      if (u)
        return u.reduce(
          (k, w) => E({ ...w, text: k }),
          E({ ...g, children: g.children })
        );
      const f = c.attrs || {};
      if (a) {
        const k = e.get("txt") || 0;
        e.set("txt", k + 1), f.key = `txt-${k}`;
      }
      return r(g.text, f);
    }
    return "";
  }, z = (c, u) => {
    const { linktype: g, href: f, anchor: k, ...w } = c.attrs || {};
    let $ = "";
    switch (g) {
      case j.ASSET:
      case j.URL:
        $ = f;
        break;
      case j.EMAIL:
        $ = `mailto:${f}`;
        break;
      case j.STORY:
        $ = f, k && ($ = `${$}#${k}`);
        break;
      default:
        $ = f;
        break;
    }
    const T = { ...w };
    return $ && (T.href = $), u.render("a", T, c.text);
  }, se = (c, u) => {
    var g, f;
    return console.warn("[StoryblokRichtText] - BLOK resolver is not available for vanilla usage"), u.render("span", {
      blok: (g = c == null ? void 0 : c.attrs) == null ? void 0 : g.body[0],
      id: (f = c.attrs) == null ? void 0 : f.id,
      style: "display: none"
    });
  }, oe = (c, u) => {
    const g = {}, f = u.render("tbody", {}, c.children);
    return u.render("table", g, f);
  }, ne = (c, u) => {
    const g = {};
    return u.render("tr", g, c.children);
  }, ae = (c, u) => {
    const { colspan: g, rowspan: f, colwidth: k, backgroundColor: w, ...$ } = c.attrs || {}, T = {
      ...$
    };
    g > 1 && (T.colspan = g), f > 1 && (T.rowspan = f);
    const R = [];
    return k && R.push(`width: ${k}px;`), w && R.push(`background-color: ${w};`), R.length > 0 && (T.style = R.join(" ")), u.render("td", O(T), c.children);
  }, ie = (c, u) => {
    const { colspan: g, rowspan: f, colwidth: k, backgroundColor: w, ...$ } = c.attrs || {}, T = {
      ...$
    };
    g > 1 && (T.colspan = g), f > 1 && (T.rowspan = f);
    const R = [];
    return k && R.push(`width: ${k}px;`), w && R.push(`background-color: ${w};`), R.length > 0 && (T.style = R.join(" ")), u.render("th", O(T), c.children);
  }, le = new Map([
    [v.DOCUMENT, h("")],
    [v.HEADING, p],
    [v.PARAGRAPH, h("p")],
    [v.UL_LIST, h("ul")],
    [v.OL_LIST, h("ol")],
    [v.LIST_ITEM, h("li")],
    [v.IMAGE, d],
    [v.EMOJI, b],
    [v.CODE_BLOCK, S],
    [v.HR, h("hr")],
    [v.BR, h("br")],
    [v.QUOTE, h("blockquote")],
    [v.COMPONENT, se],
    [Z.TEXT, I],
    [_.LINK, z],
    [_.ANCHOR, z],
    [_.STYLED, y("span", !0)],
    [_.BOLD, y("strong")],
    [_.TEXT_STYLE, y("span", !0)],
    [_.ITALIC, y("em")],
    [_.UNDERLINE, y("u")],
    [_.STRIKE, y("s")],
    [_.CODE, y("code")],
    [_.SUPERSCRIPT, y("sup")],
    [_.SUBSCRIPT, y("sub")],
    [_.HIGHLIGHT, y("mark")],
    [v.TABLE, oe],
    [v.TABLE_ROW, ne],
    [v.TABLE_CELL, ae],
    [v.TABLE_HEADER, ie],
    ...Object.entries(o).map(([c, u]) => [c, u])
  ]);
  function C(c) {
    const u = le.get(c.type);
    if (!u)
      return console.error("<Storyblok>", `No resolver found for node type ${c.type}`), "";
    const g = l();
    if (c.type === "text")
      return u(c, g);
    const f = c.content ? c.content.map(H) : void 0;
    return u({
      ...c,
      children: f
    }, g);
  }
  function H(c) {
    return c.type === "doc" ? i ? c.content.map(C) : c.content.map(C).join("") : Array.isArray(c) ? c.map(C) : C(c);
  }
  return {
    render: H
  };
}
let B, J = "https://app.storyblok.com/f/storyblok-v2-latest.js";
const at = (s, e, t = {}) => {
  var r;
  const o = !(typeof window > "u") && typeof window.storyblokRegisterEvent < "u", n = new URL((r = window.location) == null ? void 0 : r.href).searchParams.get(
    "_storyblok"
  ), a = n !== null && +n === s;
  if (!(!o || !a)) {
    if (!s) {
      console.warn("Story ID is not defined. Please provide a valid ID.");
      return;
    }
    window.storyblokRegisterEvent(() => {
      new window.StoryblokBridge(t).on(["input", "published", "change"], (i) => {
        var l;
        i && (i.action === "input" && ((l = i.story) == null ? void 0 : l.id) === s ? e(i.story) : (i.action === "change" || i.action === "published") && i.storyId === s && window.location.reload());
      });
    });
  }
}, ee = (s, e) => {
  s.addNode("blok", (t) => {
    let r = "";
    return t.attrs.body.forEach((o) => {
      r += e(o.component, o);
    }), {
      html: r
    };
  });
}, it = (s = {}) => {
  var e, t;
  const {
    bridge: r,
    accessToken: o,
    use: n = [],
    apiOptions: a = {},
    richText: i = {},
    bridgeUrl: l
  } = s;
  a.accessToken = a.accessToken || o;
  const h = { bridge: r, apiOptions: a };
  let d = {};
  n.forEach((b) => {
    d = { ...d, ...b(h) };
  }), l && (J = l);
  const p = !(typeof window > "u") && ((t = (e = window.location) == null ? void 0 : e.search) == null ? void 0 : t.includes("_storyblok_tk"));
  return r !== !1 && p && be(J), B = new M(i.schema), i.resolver && ee(B, i.resolver), d;
}, lt = (s) => {
  var e;
  return !s || !((e = s == null ? void 0 : s.content) != null && e.some((t) => t.content || t.type === "blok" || t.type === "horizontal_rule"));
}, ft = (s, e, t) => {
  let r = t || B;
  if (!r) {
    console.error(
      "Please initialize the Storyblok SDK before calling the renderRichText function"
    );
    return;
  }
  return lt(s) ? "" : (e && (r = new M(e.schema), e.resolver && ee(r, e.resolver)), r.render(s, {}, !1));
}, te = /* @__PURE__ */ Y({
  __name: "StoryblokComponent",
  props: {
    blok: {}
  },
  setup(s, { expose: e }) {
    const t = s, r = P();
    e({
      value: r
    });
    const o = typeof D(t.blok.component) != "string", n = de("VueSDKOptions"), a = P(t.blok.component);
    return !o && n && (n.enableFallbackComponent ? (a.value = n.customFallbackComponent ?? "FallbackComponent", typeof D(a.value) == "string" && console.error(
      `Is the Fallback component "${a.value}" registered properly?`
    )) : console.error(
      `Component could not be found for blok "${t.blok.component}"! Is it defined in main.ts as "app.component("${t.blok.component}", ${t.blok.component});"?`
    )), (i, l) => (X(), W(D(a.value), pe({
      ref_key: "blokRef",
      ref: r
    }, { ...i.$props, ...i.$attrs }), null, 16));
  }
}), ct = (s) => {
  var e, t;
  return Q(
    te,
    {
      blok: (e = s == null ? void 0 : s.attrs) == null ? void 0 : e.body[0],
      id: (t = s.attrs) == null ? void 0 : t.id
    },
    s.children
  );
};
function ht(s) {
  const e = {
    renderFn: Q,
    textFn: ge,
    keyedResolvers: !0,
    resolvers: {
      [v.COMPONENT]: ct,
      ...s.resolvers
    }
  };
  return nt(e);
}
const ut = /* @__PURE__ */ Y({
  __name: "StoryblokRichText",
  props: {
    doc: {},
    resolvers: {}
  },
  setup(s) {
    const e = s, t = P(), r = () => t.value;
    return fe([() => e.doc, () => e.resolvers], ([o, n]) => {
      const { render: a } = ht({
        resolvers: n ?? {}
      });
      t.value = a(o);
    }, {
      immediate: !0,
      deep: !0
    }), (o, n) => (X(), W(r));
  }
}), dt = {
  beforeMount(s, e) {
    if (e.value) {
      const t = Ze(e.value);
      Object.keys(t).length > 0 && (s.setAttribute("data-blok-c", t["data-blok-c"]), s.setAttribute("data-blok-uid", t["data-blok-uid"]), s.classList.add("storyblok__outline"));
    }
  }
}, re = (s) => {
  console.error(`You can't use ${s} if you're not loading apiPlugin. Please provide it on StoryblokVue initialization.
    `);
};
let A = null;
const mt = () => (A || re("useStoryblokApi"), A), yt = async (s, e = {}, t = {}) => {
  const r = P(null);
  if (t.resolveRelations = t.resolveRelations ?? e.resolve_relations, t.resolveLinks = t.resolveLinks ?? e.resolve_links, me(() => {
    r.value && r.value.id && at(
      r.value.id,
      (o) => r.value = o,
      t
    );
  }), A) {
    const { data: o } = await A.get(
      `cdn/stories/${s}`,
      e
    );
    r.value = o.story;
  } else
    re("useStoryblok");
  return r;
}, bt = {
  install(s, e = {}) {
    s.directive("editable", dt), s.component("StoryblokComponent", te), s.component("StoryblokRichText", ut), e.enableFallbackComponent && !e.customFallbackComponent && s.component(
      "FallbackComponent",
      ye(() => import("./FallbackComponent-Dky11gEu.js"))
    );
    const { storyblokApi: t } = it(e);
    A = t || null, s.provide("VueSDKOptions", e);
  }
};
export {
  v as BlockTypes,
  _ as MarkTypes,
  M as RichTextResolver,
  Ye as RichTextSchema,
  te as StoryblokComponent,
  ut as StoryblokRichText,
  bt as StoryblokVue,
  Z as TextTypes,
  gt as apiPlugin,
  ft as renderRichText,
  nt as richTextResolver,
  yt as useStoryblok,
  mt as useStoryblokApi,
  at as useStoryblokBridge,
  ht as useStoryblokRichText
};
